import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { lovable } from '@/integrations/lovable/index';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithGoogle: (redirectTo?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const initializationComplete = useRef(false);

  useEffect(() => {
    let isMounted = true;
    
    // Set up auth state change listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
      console.log("Auth state change:", event, !!newSession);
      
      if (!isMounted) return;
      
      // Always update session and user on auth state changes
      setSession(newSession);
      setUser(newSession?.user ?? null);
      
      // Only set loading to false after initialization is complete
      // This prevents premature loading=false during the initial check
      if (initializationComplete.current) {
        setLoading(false);
      }
    });

    // Initialize auth state
    const initializeAuth = async () => {
      try {
        const rememberMe = localStorage.getItem("rememberMe");
        const sessionActive = sessionStorage.getItem("sessionActive");
        
        // Get current session
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        
        if (!isMounted) return;
        
        // Check if user should be signed out (no remember me, new browser session)
        if (!rememberMe && !sessionActive && currentSession) {
          console.log("Signing out: no remember me preference");
          // Mark initialization complete before sign out
          // This ensures the SIGNED_OUT event will set loading to false
          initializationComplete.current = true;
          await supabase.auth.signOut();
          // The onAuthStateChange will handle setting state
          return;
        }
        
        // Set initial state
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        
      } catch (error) {
        console.error("Error initializing auth:", error);
      } finally {
        if (isMounted) {
          initializationComplete.current = true;
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({ email, password });
    return { error };
  };

  const signInWithGoogle = async (redirectTo?: string) => {
    // Set session flags so the remember-me logic doesn't sign user out after OAuth redirect
    localStorage.setItem("rememberMe", "true");
    sessionStorage.setItem("sessionActive", "true");

    const isCustomDomain =
      !window.location.hostname.includes("lovable.app") &&
      !window.location.hostname.includes("lovableproject.com") &&
      !window.location.hostname.includes("localhost");

    if (isCustomDomain) {
      // On custom domains, bypass auth-bridge — it can't relay tokens back properly
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/oauth-success`,
          skipBrowserRedirect: true,
        },
      });

      if (error) return { error };

      if (data?.url) {
        window.location.href = data.url;
      }

      return { error: null };
    } else {
      // On Lovable domains, use the managed auth-bridge flow
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: `${window.location.origin}/oauth-success`,
        extraParams: {
          prompt: 'select_account',
        },
      });
      return { error: result.error ?? null };
    }
  };

  const signOut = async () => {
    // Clear remember me flags on sign out
    localStorage.removeItem("rememberMe");
    sessionStorage.removeItem("sessionActive");
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signIn, signUp, signInWithGoogle, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
